import { useState } from "react";

interface Props {
  publicUrl: string | null;
  tenantSlug: string | null;
  isPublished: boolean;
}

type Device = "desktop" | "tablet" | "mobile";

const DEVICE_CONFIG: Record<Device, { label: string; icon: string; width: string; frameW: number }> = {
  desktop: { label: "Desktop", icon: "ri-computer-line", width: "100%", frameW: 1280 },
  tablet:  { label: "Tablet",  icon: "ri-tablet-line",  width: "768px", frameW: 768 },
  mobile:  { label: "Mobile",  icon: "ri-smartphone-line", width: "390px", frameW: 390 },
};

export default function PreviewTab({ publicUrl, tenantSlug, isPublished }: Props) {
  const [device, setDevice] = useState<Device>("desktop");
  const [refreshKey, setRefreshKey] = useState(0);

  const fullUrl = publicUrl ? `${window.location.origin}${publicUrl}` : null;
  const cfg = DEVICE_CONFIG[device];

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          {/* Device switcher */}
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1">
            {(Object.entries(DEVICE_CONFIG) as [Device, typeof DEVICE_CONFIG[Device]][]).map(([key, val]) => (
              <button
                key={key}
                onClick={() => setDevice(key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${device === key ? "bg-[#C8A96E] text-white" : "text-slate-500 hover:text-slate-700"}`}
              >
                <i className={val.icon}></i>
                {val.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => setRefreshKey(k => k + 1)}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-slate-200 text-slate-400 hover:text-slate-600 hover:border-slate-300 transition-all cursor-pointer"
            title="Refresh preview"
          >
            <i className="ri-refresh-line text-sm"></i>
          </button>
        </div>

        <div className="flex items-center gap-2">
          {fullUrl && (
            <a
              href={fullUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-sm text-[#9a7840] hover:text-[#7a5e2a] border border-[#C8A96E]/30 hover:border-[#C8A96E]/50 bg-[#C8A96E]/8 hover:bg-[#C8A96E]/15 px-3 py-1.5 rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-external-link-line"></i>
              Open in new tab
            </a>
          )}
          {!isPublished && (
            <span className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-lg whitespace-nowrap">
              <i className="ri-eye-off-line"></i>
              Page is hidden
            </span>
          )}
        </div>
      </div>

      {/* URL bar */}
      {fullUrl && (
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
          <div className="flex items-center gap-1 flex-shrink-0">
            <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-amber-400"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-400"></div>
          </div>
          <div className="flex-1 bg-slate-100 rounded-md px-3 py-1 text-xs text-slate-500 font-mono truncate">
            {fullUrl}
          </div>
          <i className="ri-lock-line text-slate-300 text-xs flex-shrink-0"></i>
        </div>
      )}

      {/* Preview frame */}
      {!tenantSlug ? (
        <div className="flex flex-col items-center gap-4 py-20 bg-white rounded-xl border border-slate-200">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-amber-50">
            <i className="ri-global-line text-amber-400 text-2xl"></i>
          </div>
          <div className="text-center">
            <p className="text-slate-600 font-semibold text-sm mb-1">No public URL configured</p>
            <p className="text-slate-400 text-xs max-w-xs">Your studio doesn&apos;t have a slug yet. Contact a Super Admin to assign one before your public page is accessible.</p>
          </div>
        </div>
      ) : (
        <div className="bg-slate-100 rounded-xl border border-slate-200 overflow-hidden" style={{ minHeight: "600px" }}>
          <div className="flex justify-center p-4" style={{ minHeight: "600px" }}>
            <div
              className="bg-white rounded-lg overflow-hidden border border-slate-200 transition-all duration-300"
              style={{ width: cfg.width, maxWidth: "100%", minHeight: "580px" }}
            >
              <iframe
                key={refreshKey}
                src={`${window.location.origin}${publicUrl}`}
                title="Public site preview"
                className="w-full border-0"
                style={{ height: "580px", minHeight: "580px" }}
                sandbox="allow-scripts allow-same-origin allow-forms"
              />
            </div>
          </div>
        </div>
      )}

      {/* Tips */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex items-start gap-3">
          <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#C8A96E]/10 flex-shrink-0">
            <i className="ri-lightbulb-line text-[#9a7840] text-sm"></i>
          </div>
          <div>
            <p className="text-slate-600 text-xs font-medium mb-1">Preview Tips</p>
            <ul className="space-y-1 text-slate-400 text-xs">
              <li>• Changes are live immediately after saving — refresh the preview to see them.</li>
              <li>• The preview shows the actual public page your clients will see.</li>
              <li>• Use &quot;Open in new tab&quot; to test the full interactive experience.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
